import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  limit,
  getDocs,
  startAfter,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, translateText, transcribeAudio } from "../firebase";
import { LANGUAGES } from "../theme";

// All supported language codes — translate to every language except sender's
const ALL_LANG_CODES = Object.keys(LANGUAGES); // ["en", "es", "tl", "ceb"]

// How many messages per page (real-time window + each pagination batch)
const PAGE_SIZE = 50;

export function useMessages(conversationId, participantLanguages = null) {
  // Real-time window: the newest PAGE_SIZE messages, kept live by onSnapshot
  const [realtimeMessages, setRealtimeMessages] = useState([]);
  // Earlier messages: loaded on demand via "Load earlier" pagination
  const [earlierMessages, setEarlierMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  // Track the oldest realtime doc's createdAt for pagination cursor
  const oldestRealtimeRef = useRef(null);

  // Real-time listener — fetches newest PAGE_SIZE messages (desc) then reverses
  useEffect(() => {
    if (!conversationId) {
      setRealtimeMessages([]);
      setEarlierMessages([]);
      setLoading(false);
      setHasMore(true);
      setError(null);
      oldestRealtimeRef.current = null;
      return;
    }
    setError(null);

    const q = query(
      collection(db, "conversations", conversationId, "messages"),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs;
        // docs are in desc order (newest first); reverse to asc for display
        const msgs = docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
        setRealtimeMessages(msgs);
        // Track oldest doc for pagination cursor
        oldestRealtimeRef.current = docs.length > 0 ? docs[docs.length - 1] : null;
        // If first load returned fewer than PAGE_SIZE, there are no earlier messages
        if (docs.length < PAGE_SIZE) setHasMore(false);
        setLoading(false);
      },
      (err) => {
        // A listener error (e.g. permission-denied from the org wall) is
        // terminal — the snapshot stream is dead until the effect re-runs.
        console.error("Messages listener error:", err);
        setError(err);
        setLoading(false);
      }
    );

    return unsub;
  }, [conversationId]);

  // Load earlier messages (one-time fetch, prepended to history)
  const loadEarlier = useCallback(async () => {
    if (!conversationId || loadingEarlier || !hasMore) return;

    // Determine the pagination cursor: oldest earlier message, or oldest realtime
    const cursorDoc = oldestRealtimeRef.current;
    if (!cursorDoc) return;

    setLoadingEarlier(true);
    try {
      const q = query(
        collection(db, "conversations", conversationId, "messages"),
        orderBy("createdAt", "desc"),
        startAfter(cursorDoc),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const older = snap.docs.map((d) => ({ id: d.id, ...d.data() })).reverse();

      if (snap.docs.length < PAGE_SIZE) setHasMore(false);
      // Update cursor to the oldest we've now loaded
      if (snap.docs.length > 0) {
        oldestRealtimeRef.current = snap.docs[snap.docs.length - 1];
      }

      setEarlierMessages((prev) => [...older, ...prev]);
    } catch (err) {
      console.error("Failed to load earlier messages:", err);
    }
    setLoadingEarlier(false);
  }, [conversationId, loadingEarlier, hasMore]);

  // Merge earlier + realtime, deduplicated, in chronological order
  const messages = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const msg of [...earlierMessages, ...realtimeMessages]) {
      if (!seen.has(msg.id)) {
        seen.add(msg.id);
        combined.push(msg);
      }
    }
    return combined;
  }, [earlierMessages, realtimeMessages]);

  // Target only the languages actually spoken by other participants (deduped).
  // Fallback to translate-all when participantLanguages hasn't loaded yet
  // (preserves 1:1 legacy behavior + belt-and-suspenders on missing meta).
  const getTargetLangs = (senderLang) => {
    if (participantLanguages && Object.keys(participantLanguages).length > 0) {
      const needed = new Set(Object.values(participantLanguages));
      needed.delete(senderLang);
      return [...needed];
    }
    return ALL_LANG_CODES.filter((code) => code !== senderLang);
  };

  // Send a text message with translation to ALL supported languages
  const sendMessage = useCallback(
    async ({ senderUid, senderLang, text, quickMode = false, replyTo }) => {
      if (!conversationId || !text.trim()) return;

      const targetLangs = getTargetLangs(senderLang);
      const needsTranslation = targetLangs.length > 0;

      let original = text.trim();
      let translations = {};
      let translationPending = false;

      if (needsTranslation) {
        try {
          const { data } = await translateText({ text, targetLangs, quickMode });
          original = data.original;
          translations = data.translations || {};
        } catch (err) {
          // Offline or translate() unreachable — write the message with sender text only
          // so Firestore's offline queue can sync it on reconnect. Mark it so a future
          // re-translate pass can identify and fill in translations.
          console.warn("Translate failed, sending untranslated (will queue if offline):", err);
          translationPending = true;
        }
      }

      // Build message doc
      const msgData = {
        senderUid,
        senderLang,
        originalText: original,
        translations,
        // Keep translatedText for backward compat with older messages
        translatedText: Object.values(translations)[0] || original,
        translationPending,
        isAudio: false,
        photoURL: null,
        createdAt: serverTimestamp(),
      };

      if (replyTo) {
        msgData.replyToId = replyTo.id;
        msgData.replyToText = replyTo.text;
        msgData.replyToSenderName = replyTo.senderName;
      }

      const msgRef = await addDoc(
        collection(db, "conversations", conversationId, "messages"),
        msgData
      );

      await updateDoc(doc(db, "conversations", conversationId), {
        lastMessage: original,
        lastMessageAt: serverTimestamp(),
        lastMessageSenderUid: senderUid,
      });

      return msgRef.id;
    },
    [conversationId, participantLanguages]
  );

  // Send a photo attachment
  const sendPhoto = useCallback(
    async ({ senderUid, senderLang, file }) => {
      if (!conversationId || !file) return;

      // Upload to Firebase Storage
      const storagePath = `chat-attachments/${conversationId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const photoURL = await getDownloadURL(storageRef);

      // Write message
      await addDoc(
        collection(db, "conversations", conversationId, "messages"),
        {
          senderUid,
          senderLang,
          originalText: "",
          translatedText: "",
          isAudio: false,
          photoURL,
          createdAt: serverTimestamp(),
        }
      );

      await updateDoc(doc(db, "conversations", conversationId), {
        lastMessage: "📷 Photo",
        lastMessageAt: serverTimestamp(),
        lastMessageSenderUid: senderUid,
      });
    },
    [conversationId]
  );

  // Send a voice memo (upload → transcribe → translate to ALL languages → save)
  const sendVoiceMemo = useCallback(
    async ({ senderUid, senderLang, audioBlob }) => {
      if (!conversationId || !audioBlob) return;

      const storagePath = `voice-memos/${senderUid}/${Date.now()}.webm`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, audioBlob);

      const { data: transcription } = await transcribeAudio({ storagePath, senderLang });

      const detectedLang = transcription.detectedLanguage || senderLang;
      const targetLangs = getTargetLangs(detectedLang);
      const needsTranslation = targetLangs.length > 0;

      let original = transcription.text;
      let translations = {};

      if (needsTranslation) {
        const { data: translation } = await translateText({
          text: transcription.text,
          targetLangs,
        });
        original = translation.original;
        translations = translation.translations || {};
      }

      await addDoc(
        collection(db, "conversations", conversationId, "messages"),
        {
          senderUid,
          senderLang: detectedLang,
          originalText: original,
          translations,
          translatedText: Object.values(translations)[0] || original,
          isAudio: true,
          photoURL: null,
          createdAt: serverTimestamp(),
        }
      );

      await updateDoc(doc(db, "conversations", conversationId), {
        lastMessage: `🎤 ${transcription.text.substring(0, 40)}...`,
        lastMessageAt: serverTimestamp(),
        lastMessageSenderUid: senderUid,
      });
    },
    [conversationId, participantLanguages]
  );

  return { messages, loading, loadingEarlier, hasMore, error, loadEarlier, sendMessage, sendPhoto, sendVoiceMemo };
}
